import { useSelector } from 'react-redux';
import {
  Typography,
  CardContent,
  Box,
  Divider,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';

import defaultImage from '@shared/assets/slides-noImg/no-hotel1.png';

import BookingHeader from './BookingHeader';
import BookingImage from './BookingImage';
import BookingBasicInfo from './BookingBasicInfo';
import BookingRoomsList from './BookingRoomsList';
import BookingMapButton from './BookingMapButton';

import SeeRefundability from '../helpers/SeeRefundability';
import CancellationPolicies from '../helpers/CancellationPolicies';
import BookingPriceSummary from '../helpers/BookingPriceSummary';

import BoxContainer from '@shared/components/BoxContainer';

import rawGetFormattedPrice from '@shared/helpers/rawGetFormattedPrice';
import InfoValueRow from '@shared/components/InfoValueRow';
import React from 'react';

function BookingDetail({
  room = {},
  hotelDetails = null,
  searchData,
  cancelationFee,
  isaBuying,
  hotelsValidateResult,
  price,
  cardData,
  isPayment = false,
}) {
  const { business_types } = useSelector(store => store.pageConfiguration);

  const hasRemarks =
    room?.general_comments?.length > 0 ||
    hotelsValidateResult?.comments?.length > 0 ||
    hotelsValidateResult?.remarks?.length > 0;

  const showTarifa = business_types.id !== 2;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const DetailBlock = (
    <>
      <Typography
        sx={{
          color: '#000',
          fontSize: { xs: '18px', sm: '20px' },
          fontWeight: 700,
          paddingBottom: '20px',
          lineHeight: 1,
        }}
      >
        Detalle de tu reserva
      </Typography>

      <BookingHeader name={hotelDetails.name} rating={hotelDetails.rating} />

      <BookingImage
        image={hotelDetails.images?.[0] || defaultImage}
        title={hotelDetails.name}
      />

      <CardContent
        sx={{ pt: { xs: 2, sm: 3 }, p: 0, '&:last-child': { pb: 0 } }}
      >
        <Tooltip
          title={
            <Box sx={{ p: 1, maxHeight: 250, overflowY: 'auto' }}>
              <BookingRoomsList room={room} />
            </Box>
          }
          placement="top-end"
          PopperProps={{
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [-50, 0],
                },
              },
            ],
          }}
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: '#fff',
                color: '#000',
                boxShadow: 1,
                maxWidth: '500px',
              },
            },
            arrow: {
              sx: {
                color: '#fff',
              },
            },
          }}
        >
          <Box>
            <BookingBasicInfo
              hotelDetails={hotelDetails}
              room={room}
              searchData={searchData}
            />
          </Box>
        </Tooltip>

        <Divider sx={{ my: 2, borderColor: '#ccc' }} />

        <SeeRefundability
          index={'BookingDetailReserve'}
          check_in={searchData.checkIn}
          cancelationFee={cancelationFee}
          taxAlign={'start'}
          textDirection={'row'}
          textPadding={0}
          subtitleTransform={'lowercase'}
          isaBuying={isaBuying}
          hotelCancelationFeeError={cancelationFee?.error}
        />

        {(hotelsValidateResult?.cancellation_policies?.length > 0 ||
          hasRemarks) && <Divider sx={{ my: 2, borderColor: '#ccc' }} />}

        <CancellationPolicies hotelsValidateResult={hotelsValidateResult} />

        {isMobile && (
          <>
            <Divider sx={{ my: 2, borderColor: '#ccc' }} />

            <BookingRoomsList room={room} />
          </>
        )}

        <BookingMapButton hotelDetails={hotelDetails} />
      </CardContent>
    </>
  );

  const TarifaBlock = showTarifa && (
    <Box sx={{ width: '100%' }}>
      <Typography
        sx={{
          color: '#000',
          fontWeight: 700,
          lineHeight: 1,
          mb: '2px',
          fontSize: { xs: '18px', sm: '20px' },
        }}
      >
        Precio final
      </Typography>

      <InfoValueRow
        label="Corresponde a"
        value={rawGetFormattedPrice({ price, business_types })}
        isPrice
        formatted
      />
    </Box>
  );

  const PriceBlock = (
    <BookingPriceSummary
      price={price}
      isPayment={isPayment}
      cardData={cardData}
    />
  );

  let sections;

  if (isPayment) {
    sections = [
      { key: 'price', node: PriceBlock },
      { key: 'tarifa', node: TarifaBlock },
      { key: 'detail', node: DetailBlock },
    ];
  } else {
    sections = [
      { key: 'detail', node: DetailBlock },
      { key: 'tarifa', node: TarifaBlock },
      { key: 'price', node: PriceBlock },
    ];
  }

  return (
    <BoxContainer gap={3}>
      {sections
        .filter(section => Boolean(section.node))
        .map(section => (
          <React.Fragment key={section.key}>{section.node}</React.Fragment>
        ))}
    </BoxContainer>
  );
}

export default BookingDetail;
